/**
 * Anonymous Flagged Token Monitoring — A6-S1/S2/S3.
 *
 * Session-scoped flag tokens only. The screen never resolves a token to an
 * identity path; notes and exports are behaviour-only operational records.
 */

import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type {
  DensitySnapshot,
  FlagActivity,
  FlagAlertEvent,
  FlagPriority,
  FlagRecord,
  FlagZoneVisit,
  Zone,
  ZoneId,
} from '@vigilo/shared';
import { useApp } from '@/shell/AppContext';
import { useMockMode } from '@/shell/MockModeContext';
import {
  type ColorPalette,
  Icons,
  KLabel,
  StatusDot,
  Btn,
  FONT_SIZE,
  FONT_WEIGHT,
  LETTER_SPACING,
  LINE_HEIGHT,
  RADIUS,
  SPACE,
  SIZE,
} from '@vigilo/design-system';
import { getCrowdC } from '../../design/crowdPalette';
import { MOCK_DENSITY, MOCK_ZONES } from '../data/mock';
import {
  CROWD_CLIENT_ACTIVITY_ACTION,
  CROWD_CLIENT_RESOURCE_TYPE,
  useCrowdActivityAudit,
  useCrowdFilterActivity,
} from '../hooks/useCrowdActivityAudit';
import { useDensityStream } from '../hooks/useDensityStream';
import { useLiveZoneMetrics, zoneMetricsDensitySnapshots } from '../hooks/useLiveZoneMetrics';
import { FLAG_LIMIT, useFlags } from '../hooks/useFlags';
import { useZones } from '../hooks/useZones';
import { PaginationControls } from '../components/PaginationControls';
import { useLocalPagination } from '../hooks/useLocalPagination';
import { ToastContainer, useToast } from '../components/Toast';

const NOTE_LIMIT = 140;

type PriorityFilter = 'all' | FlagPriority;
const FLAG_PRIORITY_OPTIONS: readonly FlagPriority[] = ['watch', 'urgent'];
const PRIORITY_FILTERS: readonly PriorityFilter[] = ['all', 'urgent', 'watch'];

interface FlaggingLocationState {
  openCreateModal?: boolean;
  zoneId?: string;
}

interface ActivityRow {
  id: string;
  kind: 'entry' | 'exit' | 'dwell_exceeded' | 'flagged_token_entry' | 'flagged_token_reentry';
  zoneName: string;
  timestamp: string;
  duration?: string;
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function shortToken(tokenRef: string): string {
  return `${tokenRef.slice(0, 8)}...`;
}

function zoneTypeLabel(type: string): string {
  return type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function priorityColor(priority: FlagPriority, C: ColorPalette): string {
  return priority === 'urgent' ? C.critical : C.amber;
}

function priorityLabel(priority: PriorityFilter): string {
  return `${priority.charAt(0).toUpperCase()}${priority.slice(1)}`;
}

function flagCountColor(count: number, C: ColorPalette): string {
  if (count >= FLAG_LIMIT) return C.ember;
  if (count > FLAG_LIMIT * 0.75) return C.amber;
  return C.muted;
}

function capColor(count: number, C: ColorPalette): string {
  if (count >= 19) return C.ember;
  if (count >= 10) return C.amber;
  return C.green;
}

function getZone(zones: Zone[], zoneId: ZoneId | null | undefined): Zone | undefined {
  return zoneId ? zones.find(zone => zone.id === zoneId) : undefined;
}

function buildActivityRows(activity?: FlagActivity): ActivityRow[] {
  if (!activity) return [];

  const rows: ActivityRow[] = [];
  activity.zonesVisited.forEach((visit: FlagZoneVisit, index) => {
    rows.push({
      id: `${activity.flagId}-entry-${index}`,
      kind: 'entry',
      zoneName: visit.zoneName,
      timestamp: visit.entryAt,
    });
    if (visit.exitAt) {
      rows.push({
        id: `${activity.flagId}-exit-${index}`,
        kind: 'exit',
        zoneName: visit.zoneName,
        timestamp: visit.exitAt,
        duration: `${visit.dwellMinutes}m`,
      });
    }
  });

  activity.alertEvents.forEach((event: FlagAlertEvent, index) => {
    const visit = activity.zonesVisited.find(v => v.zoneId === event.zoneId);
    rows.push({
      id: `${activity.flagId}-alert-${index}`,
      kind: event.eventType === 'reentry'
        ? 'flagged_token_reentry'
        : event.eventType === 'dwell_exceeded'
          ? 'dwell_exceeded'
          : 'flagged_token_entry',
      zoneName: visit?.zoneName ?? event.zoneId,
      timestamp: event.timestamp,
      duration: event.eventType === 'dwell_exceeded'
        ? `${visit?.dwellMinutes ?? 0}m`
        : undefined,
    });
  });

  return rows.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
}

function isBehaviourOnly(note: string): boolean {
  const patterns = [
    /\b[A-Z][a-z]+\s+[A-Z][a-z]+\b/,
    /\b[A-Z]\.\s?[A-Z][a-z]+\b/,
    /\b[A-Z]{2}\d{2}\s?[A-Z]{3}\b/i,
    /\b(?:male|female|man|woman|boy|girl)\b/i,
    /\b(?:wearing|shirt|jacket|hair|beard|tattoo)\b/i,
    /\b(?:passport|license|licence|national id|driver)\b/i,
    /\b\d{10,}\b/,
    /\b(?:imsi|imei|msisdn|phone|mobile|subscriber)\b/i,
    /\b(?:case|warrant|investigation|investigative|suspect|forensics?|evidence)\b/i,
  ];
  return !patterns.some(pattern => pattern.test(note));
}

function Field({ C, label, children }: { C: ColorPalette; label: string; children: React.ReactNode }) {
  return (
    <div>
      <KLabel C={C} mb={SPACE[6]}>{label}</KLabel>
      {children}
    </div>
  );
}

function PriorityBadge({ priority, C }: { priority: FlagPriority; C: ColorPalette }) {
  const color = priorityColor(priority, C);
  return (
    <span style={{
      fontSize: FONT_SIZE.label,
      fontWeight: FONT_WEIGHT.bold,
      letterSpacing: LETTER_SPACING.label,
      textTransform: 'uppercase',
      color,
      background: `${color}26`,
      borderRadius: RADIUS.xs,
      padding: `${SPACE[2]}px ${SPACE[6]}px`,
      lineHeight: LINE_HEIGHT.badge,
    }}>
      {priority}
    </span>
  );
}

function PriorityButton({ active, color, label, onClick, C }: {
  active: boolean;
  color: string;
  label: string;
  onClick: () => void;
  C: ColorPalette;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        border: `1px solid ${active ? color : C.border2}`,
        borderRadius: RADIUS.pill,
        background: active ? color : C.surface,
        color: active ? '#fff' : C.sub,
        cursor: 'pointer',
        fontFamily: "'Albert Sans', sans-serif",
        fontSize: FONT_SIZE.base,
        fontWeight: FONT_WEIGHT.bold,
        letterSpacing: LETTER_SPACING.wide,
        padding: `${SPACE[6]}px ${SPACE[12]}px`,
        textTransform: 'uppercase',
      }}
    >
      {label}
    </button>
  );
}

function FlagRow({ flag, selected, activity, zones, onClick, C }: {
  flag: FlagRecord;
  selected: boolean;
  activity?: FlagActivity;
  zones: Zone[];
  onClick: () => void;
  C: ColorPalette;
}) {
  const color = priorityColor(flag.priority, C);
  const currentZone = getZone(zones, activity?.currentZoneId);

  return (
    <div
      onClick={onClick}
      style={{
        padding: `${SPACE[10]}px ${SPACE[14]}px`,
        borderBottom: `1px solid ${C.border}`,
        borderLeft: `${SIZE.severityBar}px solid ${color}`,
        background: selected ? C.panelHi : C.surface,
        cursor: 'pointer',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: SPACE[8] }}>
        <span style={{
          fontSize: FONT_SIZE.micro,
          fontFamily: 'monospace',
          color: C.muted,
          background: C.faint,
          padding: `${SPACE[2]}px ${SPACE[5]}px`,
          borderRadius: RADIUS.xs,
        }}>
          {shortToken(flag.tokenRef)}
        </span>
        <span style={{ marginLeft: 'auto' }}>
          <PriorityBadge priority={flag.priority} C={C} />
        </span>
      </div>

      <div style={{
        fontSize: FONT_SIZE.body,
        color: C.text,
        marginTop: SPACE[4],
        lineHeight: LINE_HEIGHT.relaxed,
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
      }}>
        {flag.behaviourNote}
      </div>

      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: SPACE[8],
        marginTop: SPACE[6],
      }}>
        <span style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: SPACE[5],
          fontSize: FONT_SIZE.xs,
          color: currentZone ? C.green : C.muted,
          minWidth: 0,
        }}>
          <StatusDot color={currentZone ? C.green : C.muted} size={SIZE.dot} />
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {currentZone ? currentZone.name : 'Not in monitored zone'}
          </span>
        </span>
        <span style={{
          fontSize: FONT_SIZE.micro,
          color: C.muted,
          fontFamily: 'monospace',
          flexShrink: 0,
        }}>
          {formatTime(flag.createdAt)}
        </span>
      </div>
    </div>
  );
}

function BehaviourNoteBlock({ flag, onSave, canEdit, C }: {
  flag: FlagRecord;
  onSave: (note: string) => Promise<void>;
  canEdit: boolean;
  C: ColorPalette;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(flag.behaviourNote);
  const [saving, setSaving] = useState(false);
  const color = priorityColor(flag.priority, C);
  const valid = draft.trim().length >= SPACE[10] && isBehaviourOnly(draft);

  useEffect(() => {
    setDraft(flag.behaviourNote);
    setEditing(false);
  }, [flag.flagId, flag.behaviourNote]);

  async function save() {
    if (!valid) return;
    setSaving(true);
    try {
      await onSave(draft.trim());
      setEditing(false);
    } finally {
      setSaving(false);
    }
  }

  return (
    <section style={{
      background: C.surface,
      borderRadius: RADIUS.md,
      padding: SPACE[14],
      borderLeft: `${SIZE.severityBar}px solid ${color}`,
      marginBottom: SPACE[16],
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: SPACE[8] }}>
        <KLabel C={C} mb={0}>Observed Behaviour</KLabel>
        {!editing && canEdit && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            style={{
              background: 'transparent',
              border: 'none',
              color: C.muted,
              cursor: 'pointer',
              padding: SPACE[2],
            }}
            aria-label="Edit observed behaviour"
          >
            <Icons.Edit size={SIZE.iconSm} />
          </button>
        )}
      </div>

      {editing ? (
        <div>
          <textarea
            value={draft}
            onChange={event => setDraft(event.target.value.slice(0, NOTE_LIMIT))}
            style={{
              width: '100%',
              minHeight: SIZE.textareaMin,
              background: C.panel,
              color: C.text,
              border: `1px solid ${C.border2}`,
              borderRadius: RADIUS.md,
              padding: `${SPACE[8]}px ${SPACE[10]}px`,
              fontFamily: "'Albert Sans', sans-serif",
              fontSize: FONT_SIZE.body,
              lineHeight: LINE_HEIGHT.relaxed,
              outline: 'none',
              resize: 'vertical',
              boxSizing: 'border-box',
            }}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: SPACE[6] }}>
            <span style={{ fontSize: FONT_SIZE.xs, color: valid ? C.muted : C.amber }}>
              Describe behaviour only. Avoid identity, appearance, or registration details.
            </span>
            <span style={{
              fontSize: FONT_SIZE.micro,
              color: draft.length > 120 ? C.amber : C.muted,
              fontFamily: 'monospace',
            }}>
              {draft.length} / {NOTE_LIMIT}
            </span>
          </div>
          <div style={{ display: 'flex', gap: SPACE[8], justifyContent: 'flex-end', marginTop: SPACE[10] }}>
            <Btn C={C} variant="ghost" size="sm" onClick={() => { setDraft(flag.behaviourNote); setEditing(false); }}>
              Cancel
            </Btn>
            <Btn C={C} variant="primary" size="sm" disabled={!valid || saving} onClick={() => { void save(); }}>
              {saving ? 'Saving...' : 'Save'}
            </Btn>
          </div>
        </div>
      ) : (
        <div style={{
          fontSize: FONT_SIZE.body,
          color: C.text,
          lineHeight: LINE_HEIGHT.relaxed,
        }}>
          {flag.behaviourNote}
        </div>
      )}
    </section>
  );
}

function MonitoredZones({ flag, zones, density, onChange, canEdit, C }: {
  flag: FlagRecord;
  zones: Zone[];
  density: Record<string, DensitySnapshot>;
  onChange: (zoneIds: ZoneId[]) => Promise<void>;
  canEdit: boolean;
  C: ColorPalette;
}) {
  const [adding, setAdding] = useState(false);
  const monitored = flag.monitoredZoneIds.map(id => getZone(zones, id)).filter((zone): zone is Zone => Boolean(zone));
  const available = zones.filter(zone => !flag.monitoredZoneIds.includes(zone.id));

  async function removeZone(zoneId: ZoneId) {
    await onChange(flag.monitoredZoneIds.filter(id => id !== zoneId));
  }

  async function addZone(zoneId: ZoneId) {
    await onChange([...flag.monitoredZoneIds, zoneId]);
    setAdding(false);
  }

  return (
    <section style={{ marginBottom: SPACE[16] }}>
      <KLabel C={C}>Monitored Zones</KLabel>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: SPACE[8] }}>
        {monitored.map(zone => {
          const util = Math.round(density[zone.id]?.capacityUtilisation ?? 0);
          return (
            <span key={zone.id} style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: SPACE[6],
              background: C.surface,
              border: `1px solid ${C.border2}`,
              borderRadius: RADIUS.pill,
              padding: `${SPACE[5]}px ${SPACE[8]}px`,
            }}>
              <span style={{ fontSize: FONT_SIZE.body, color: C.text }}>{zone.name}</span>
              <span style={{
                fontSize: FONT_SIZE.label,
                fontWeight: FONT_WEIGHT.bold,
                color: C.amber,
                textTransform: 'uppercase',
              }}>
                {zoneTypeLabel(zone.type)}
              </span>
              <span style={{ fontSize: FONT_SIZE.micro, color: C.muted, fontFamily: 'monospace' }}>
                {util}%
              </span>
              {canEdit && (
                <button
                  type="button"
                  onClick={() => { void removeZone(zone.id); }}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: C.muted,
                    cursor: 'pointer',
                    padding: 0,
                    display: 'inline-flex',
                  }}
                  aria-label={`Remove ${zone.name}`}
                >
                  <Icons.X size={SIZE.iconXs} />
                </button>
              )}
            </span>
          );
        })}

        {canEdit && (
          <div style={{ position: 'relative' }}>
            <Btn C={C} variant="ghost" size="sm" icon={<Icons.Plus size={SIZE.iconSm} />} onClick={() => setAdding(prev => !prev)}>
              Add Zone
            </Btn>
            {adding && available.length > 0 && (
              <div style={{
                position: 'absolute',
                top: `calc(100% + ${SPACE[4]}px)`,
                left: 0,
                minWidth: SIZE.flagDropdown,
                background: C.panel,
                border: `1px solid ${C.border2}`,
                borderRadius: RADIUS.card,
                zIndex: 20,
                overflow: 'hidden',
              }}>
                {available.map(zone => (
                  <button
                    key={zone.id}
                    type="button"
                    onClick={() => { void addZone(zone.id); }}
                    style={{
                      width: '100%',
                      background: 'transparent',
                      border: 'none',
                      borderBottom: `1px solid ${C.border}`,
                      color: C.text,
                      cursor: 'pointer',
                      fontFamily: "'Albert Sans', sans-serif",
                      fontSize: FONT_SIZE.body,
                      padding: `${SPACE[8]}px ${SPACE[10]}px`,
                      textAlign: 'left',
                    }}
                  >
                    {zone.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

function ActivityLog({ rows, C }: { rows: ActivityRow[]; C: ColorPalette }) {
  return (
    <section style={{ marginBottom: SPACE[16] }}>
      <KLabel C={C}>Activity Log</KLabel>
      <div style={{
        background: C.surface,
        border: `1px solid ${C.border2}`,
        borderRadius: RADIUS.panel,
        overflow: 'hidden',
      }}>
        {rows.length === 0 ? (
          <div style={{
            fontSize: FONT_SIZE.body,
            color: C.muted,
            textAlign: 'center',
            paddingTop: SPACE[20],
            paddingBottom: SPACE[20],
          }}>
            No activity recorded yet
          </div>
        ) : rows.map(row => {
          const isAlert = row.kind === 'flagged_token_entry' || row.kind === 'flagged_token_reentry';
          const dotColor = row.kind === 'entry' ? C.teal : row.kind === 'exit' ? C.amber : C.ember;
          const label = row.kind === 'entry'
            ? `Entered ${row.zoneName}`
            : row.kind === 'exit'
              ? `Exited ${row.zoneName}`
              : row.kind === 'dwell_exceeded'
                ? `Dwell exceeded in ${row.zoneName}`
                : `Warning: Flagged token ${row.kind === 'flagged_token_reentry' ? 're-entry' : 'entry'} - ${row.zoneName}`;

          return (
            <div key={row.id} style={{
              display: 'flex',
              alignItems: 'center',
              gap: SPACE[10],
              padding: `${SPACE[12]}px ${SPACE[14]}px`,
              borderBottom: `1px solid ${C.border}`,
              borderLeft: isAlert ? `${SIZE.severityBar}px solid ${C.ember}` : `${SIZE.severityBar}px solid transparent`,
            }}>
              {!isAlert && <StatusDot color={dotColor} size={SIZE.dot} />}
              <span style={{
                flex: 1,
                fontSize: FONT_SIZE.body,
                color: isAlert ? C.ember : C.text,
                fontWeight: isAlert ? FONT_WEIGHT.semibold : FONT_WEIGHT.regular,
              }}>
                {label}
                {row.duration && (
                  <span style={{ color: C.muted, marginLeft: SPACE[6] }}>{row.duration}</span>
                )}
              </span>
              <span style={{
                fontSize: FONT_SIZE.micro,
                fontFamily: 'monospace',
                color: C.muted,
                textAlign: 'right',
              }}>
                {formatTime(row.timestamp)}
              </span>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function ExportSection({ onExport, C }: {
  onExport: () => void;
  C: ColorPalette;
}) {
  return (
    <section style={{
      borderTop: `1px solid ${C.border2}`,
      paddingTop: SPACE[18],
      marginTop: SPACE[18],
    }}>
      <KLabel C={C}>Anonymous Export</KLabel>
      <div style={{
        fontSize: FONT_SIZE.body,
        color: C.muted,
        lineHeight: LINE_HEIGHT.relaxed,
        marginBottom: SPACE[12],
      }}>
        Export an anonymous behaviour log for this flag token. No subscriber identity is included, and this application does not provide an identity resolution path.
      </div>
      <Btn
        C={C}
        variant="ghost"
        icon={<Icons.Download size={SIZE.iconSm} />}
        style={{ width: '100%', justifyContent: 'center' }}
        onClick={onExport}
      >
        Export Anonymous Behaviour Log
      </Btn>
    </section>
  );
}

function FlagDetail({ flag, activity, zones, density, updateFlag, removeFlag, exportLog, canManage, C }: {
  flag: FlagRecord;
  activity?: FlagActivity;
  zones: Zone[];
  density: Record<string, DensitySnapshot>;
  updateFlag: (flagId: string, updates: { priority?: FlagPriority; behaviourNote?: string; monitoredZoneIds?: ZoneId[] }) => Promise<FlagRecord>;
  removeFlag: (flagId: string) => Promise<void>;
  exportLog: (flag: FlagRecord) => void;
  canManage: boolean;
  C: ColorPalette;
}) {
  const rows = useMemo(() => buildActivityRows(activity), [activity]);

  return (
    <div>
      <div style={{
        background: C.tealDim,
        border: `1px solid ${C.teal}55`,
        borderRadius: RADIUS.lg,
        padding: `${SPACE[10]}px ${SPACE[14]}px`,
        marginBottom: SPACE[18],
        display: 'flex',
        alignItems: 'center',
        gap: SPACE[10],
      }}>
        <Icons.Lock size={SIZE.iconMd} />
        <div style={{ fontSize: FONT_SIZE.sm, color: C.sub, lineHeight: LINE_HEIGHT.relaxed }}>
          <span style={{
            fontWeight: FONT_WEIGHT.bold,
            color: C.teal,
            letterSpacing: LETTER_SPACING.wide,
            textTransform: 'uppercase',
          }}>
            Anonymous behaviour token
          </span>
          {' - '}
          This identifier is a session-scoped internal reference. It is not resolvable to subscriber identity within this application.
        </div>
      </div>

      <header style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: SPACE[16],
        marginBottom: SPACE[18],
      }}>
        <div style={{ minWidth: 0 }}>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: SPACE[8],
            marginBottom: SPACE[8],
          }}>
            <span style={{
              fontSize: FONT_SIZE.base,
              fontFamily: 'monospace',
              color: C.muted,
              background: C.faint,
              borderRadius: RADIUS.sm,
              padding: `${SPACE[3]}px ${SPACE[8]}px`,
            }}>
              {shortToken(flag.tokenRef)}
            </span>
            <PriorityBadge priority={flag.priority} C={C} />
            <span style={{ fontSize: FONT_SIZE.base, color: C.muted }}>
              Created: {formatTime(flag.createdAt)}
            </span>
          </div>
          <h1 style={{
            margin: 0,
            fontSize: FONT_SIZE.title,
            fontWeight: FONT_WEIGHT.bold,
            color: C.text,
            lineHeight: LINE_HEIGHT.pill,
          }}>
            Anonymous Flag Detail
          </h1>
        </div>
        {canManage && (
          <div style={{ display: 'flex', gap: SPACE[6], flexShrink: 0 }}>
            <span title="Edit flag details - coming soon">
              <Btn C={C} variant="ghost" size="sm" icon={<Icons.Edit size={SIZE.iconSm} />} disabled>Edit</Btn>
            </span>
            <Btn C={C} variant="ghost" size="sm" icon={<Icons.Trash size={SIZE.iconSm} />} style={{ color: C.ember }} onClick={() => { void removeFlag(flag.flagId); }}>
              Remove
            </Btn>
          </div>
        )}
      </header>

      <BehaviourNoteBlock flag={flag} C={C} canEdit={canManage} onSave={async note => { await updateFlag(flag.flagId, { behaviourNote: note }); }} />

      <section style={{ marginBottom: SPACE[16] }}>
        <KLabel C={C}>Priority</KLabel>
        {canManage ? (
          <div style={{ display: 'flex', gap: SPACE[8] }}>
            {FLAG_PRIORITY_OPTIONS.map(priorityOption => (
              <PriorityButton
                key={priorityOption}
                active={flag.priority === priorityOption}
                color={priorityColor(priorityOption, C)}
                label={priorityLabel(priorityOption)}
                C={C}
                onClick={() => { void updateFlag(flag.flagId, { priority: priorityOption }); }}
              />
            ))}
          </div>
        ) : (
          <PriorityBadge priority={flag.priority} C={C} />
        )}
      </section>

      <MonitoredZones flag={flag} zones={zones} density={density} C={C}
        canEdit={canManage}
        onChange={async zoneIds => { await updateFlag(flag.flagId, { monitoredZoneIds: zoneIds }); }} />
      <ActivityLog rows={rows} C={C} />
      <ExportSection C={C} onExport={() => exportLog(flag)} />
    </div>
  );
}

function EmptyState({ C }: { C: ColorPalette }) {
  return (
    <div style={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: SPACE[8],
      textAlign: 'center',
    }}>
      <Icons.Shield size={SIZE.iconXxl} />
      <div style={{ fontSize: FONT_SIZE.subheading, color: C.muted }}>No flag selected</div>
      <div style={{ fontSize: FONT_SIZE.body, color: C.muted }}>Select a flagged token to view activity</div>
    </div>
  );
}

function CreateFlagModal({ C, zones, density, initialZoneId, onClose, onCreate }: {
  C: ColorPalette;
  zones: Zone[];
  density: Record<string, DensitySnapshot>;
  initialZoneId?: string | null;
  onClose: () => void;
  onCreate: (dto: {
    zoneId: string;
    behaviourNote: string;
    priority: FlagPriority;
    monitoredZoneIds: string[];
  }) => Promise<void>;
}) {
  const [note, setNote] = useState('');
  const [priority, setPriority] = useState<FlagPriority>('watch');
  const [selectedZones, setSelectedZones] = useState<string[]>([]);
  const touched = note.length > 0;
  const valid = note.trim().length >= SPACE[10] && isBehaviourOnly(note) && selectedZones.length > 0;

  useEffect(() => {
    if (!initialZoneId || selectedZones.length > 0) return;
    if (zones.some(zone => zone.id === initialZoneId)) setSelectedZones([initialZoneId]);
  }, [initialZoneId, selectedZones.length, zones]);

  function toggleZone(zoneId: string) {
    setSelectedZones(prev => prev.includes(zoneId)
      ? prev.filter(id => id !== zoneId)
      : [...prev, zoneId]);
  }

  async function submit() {
    if (!valid) return;
    await onCreate({
      zoneId: selectedZones[0],
      behaviourNote: note.trim(),
      priority,
      monitoredZoneIds: selectedZones,
    });
    onClose();
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.7)',
        backdropFilter: 'blur(4px)',
        zIndex: 2000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: SPACE[20],
      }}
    >
      <div
        onClick={event => event.stopPropagation()}
        style={{
          width: SIZE.flagModal,
          maxWidth: '90vw',
          background: C.panel,
          border: `1px solid ${C.border2}`,
          borderRadius: RADIUS.panel,
          padding: SPACE[24],
          boxSizing: 'border-box',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: SPACE[16], marginBottom: SPACE[20] }}>
          <div>
            <div style={{
              fontSize: FONT_SIZE.heading,
              fontWeight: FONT_WEIGHT.bold,
              color: C.text,
              marginBottom: SPACE[6],
            }}>
              Flag Token for Monitoring
            </div>
            <div style={{ fontSize: FONT_SIZE.body, color: C.muted, lineHeight: LINE_HEIGHT.relaxed }}>
              Describe the anomalous behaviour observed. No identity information should be recorded.
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{ background: 'transparent', border: 'none', color: C.muted, cursor: 'pointer', padding: SPACE[2] }}
            aria-label="Close"
          >
            <Icons.X size={SIZE.icon} />
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE[18] }}>
          <Field C={C} label="Describe the behaviour observed">
            <textarea
              value={note}
              onChange={event => setNote(event.target.value.slice(0, NOTE_LIMIT))}
              placeholder="e.g. Repeated re-entry into restricted zone, dwell time significantly exceeding zone average..."
              style={{
                width: '100%',
                minHeight: SIZE.textareaMin,
                background: C.surface,
                color: C.text,
                border: `1px solid ${C.border2}`,
                borderRadius: RADIUS.md,
                padding: `${SPACE[8]}px ${SPACE[10]}px`,
                fontFamily: "'Albert Sans', sans-serif",
                fontSize: FONT_SIZE.body,
                lineHeight: LINE_HEIGHT.relaxed,
                outline: 'none',
                resize: 'vertical',
                boxSizing: 'border-box',
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: SPACE[4] }}>
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: SPACE[4],
                fontSize: FONT_SIZE.xs,
                color: touched ? C.amber : C.muted,
              }}>
                {touched && <Icons.Alert size={SIZE.iconSm} />}
                Do not record names, physical descriptions, or any identifying information - describe behaviour only.
              </span>
              <span style={{ fontSize: FONT_SIZE.micro, color: note.length > 120 ? C.amber : C.muted, fontFamily: 'monospace' }}>
                {note.length} / {NOTE_LIMIT}
              </span>
            </div>
          </Field>

          <Field C={C} label="Priority Level">
            <div style={{ display: 'flex', gap: SPACE[8] }}>
              {FLAG_PRIORITY_OPTIONS.map(priorityOption => (
                <PriorityButton
                  key={priorityOption}
                  active={priority === priorityOption}
                  color={priorityColor(priorityOption, C)}
                  label={priorityLabel(priorityOption)}
                  C={C}
                  onClick={() => setPriority(priorityOption)}
                />
              ))}
            </div>
          </Field>

          <Field C={C} label="Monitor in Zones">
            <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE[6], maxHeight: SIZE.flagZoneList, overflowY: 'auto' }}>
              {zones.map(zone => {
                const checked = selectedZones.includes(zone.id);
                const util = Math.round(density[zone.id]?.capacityUtilisation ?? 0);
                return (
                  <label key={zone.id} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: SPACE[10],
                    padding: `${SPACE[8]}px ${SPACE[10]}px`,
                    borderRadius: RADIUS.md,
                    cursor: 'pointer',
                    background: checked ? C.amberDim : C.surface,
                    border: `1px solid ${checked ? `${C.amber}55` : C.border}`,
                  }}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleZone(zone.id)}
                      style={{ accentColor: C.amber }}
                    />
                    <span style={{ flex: 1, fontSize: FONT_SIZE.body, color: C.text }}>{zone.name}</span>
                    <span style={{
                      fontSize: FONT_SIZE.micro,
                      fontFamily: 'monospace',
                      color: C.muted,
                      background: C.faint,
                      borderRadius: RADIUS.xs,
                      padding: `${SPACE[2]}px ${SPACE[5]}px`,
                    }}>
                      {util}%
                    </span>
                  </label>
                );
              })}
            </div>
          </Field>
        </div>

        <footer style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: SPACE[8],
          borderTop: `1px solid ${C.border2}`,
          marginTop: SPACE[20],
          paddingTop: SPACE[14],
        }}>
          <Btn C={C} variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn C={C} variant="primary" icon={<Icons.Flag size={SIZE.iconSm} />} disabled={!valid} onClick={() => { void submit(); }}>
            Start Monitoring
          </Btn>
        </footer>
      </div>
    </div>
  );
}

export function FlaggingScreen() {
  const { theme, currentUser, selectedOperation } = useApp();
  const location = useLocation();
  const navigate = useNavigate();
  const { isMock } = useMockMode();
  const recordActivity = useCrowdActivityAudit();
  const C = getCrowdC(theme);
  const { toasts, show, dismiss } = useToast();
  const flagState = useFlags(show, { includeActivity: true });
  const { getFlagActivity, updateFlag, removeFlag, createFlag } = flagState;
  const zoneState = useZones();
  const liveDensity = useDensityStream();
  const liveZoneMetrics = useLiveZoneMetrics(
    selectedOperation?.id ?? null,
    Boolean(!isMock && selectedOperation?.status === 'active'),
    2000,
  );
  const projectedSnapshots = useMemo(() => zoneMetricsDensitySnapshots(liveZoneMetrics.rows), [liveZoneMetrics.rows]);
  const flags = isMock ? flagState.flags : flagState.flags;
  const flagActivity = isMock ? flagState.activity : flagState.activity;
  const allZones = isMock ? MOCK_ZONES : zoneState.zones;
  const zones = selectedOperation
    ? allZones.filter(z => selectedOperation.assignedZoneIds.includes(z.id))
    : allZones;
  const density = isMock ? MOCK_DENSITY : { ...liveDensity.snapshots, ...projectedSnapshots };
  const [selectedId, setSelectedId] = useState<string | null>(flags[0]?.flagId ?? null);
  const [filter, setFilter] = useState<PriorityFilter>('all');
  const [showCreate, setShowCreate] = useState(false);
  const [initialCreateZoneId, setInitialCreateZoneId] = useState<string | null>(null);
  useCrowdFilterActivity({
    page: '/flags',
    operationId: selectedOperation?.id,
    filterValues: {
      priority: filter,
    },
  });

  const scopedFlags = useMemo(() => {
    if (!selectedOperation) return flags;
    return flags.filter(flag => {
      const monitoredZoneIds = flag.monitoredZoneIds ?? [];
      return monitoredZoneIds.some(zoneId => selectedOperation.assignedZoneIds.includes(zoneId));
    });
  }, [flags, selectedOperation]);

  useEffect(() => {
    const state = location.state as FlaggingLocationState | null;
    if (!state?.openCreateModal) return;
    setInitialCreateZoneId(state.zoneId ?? null);
    setShowCreate(true);
    window.history.replaceState({}, '', location.pathname);
  }, [location.pathname, location.state]);

  useEffect(() => {
    if (selectedId && scopedFlags.some(flag => flag.flagId === selectedId)) return;
    setSelectedId(scopedFlags[0]?.flagId ?? null);
  }, [scopedFlags, selectedId]);

  useEffect(() => {
    if (selectedId) void getFlagActivity(selectedId);
  }, [getFlagActivity, selectedId]);

  const filteredFlags = scopedFlags.filter(flag => filter === 'all' || flag.priority === filter);
  const flagPagination = useLocalPagination(filteredFlags);
  const visibleFlags = flagPagination.pageRows;
  const selectedFlag = scopedFlags.find(flag => flag.flagId === selectedId);
  const canCreate = currentUser.role === 'crowd_analyst' || currentUser.role === 'command_supervisor' || currentUser.role === 'system_admin';
  const canManage = currentUser.role === 'command_supervisor' || currentUser.role === 'system_admin';
  const atLimit = scopedFlags.length >= FLAG_LIMIT;

  useEffect(() => {
    flagPagination.resetPage();
  }, [filter, flagPagination.resetPage, selectedOperation?.id]);

  function exportLog(flag: FlagRecord) {
    recordActivity({
      action: CROWD_CLIENT_ACTIVITY_ACTION.drilldownOpened,
      page: '/flags',
      operationId: selectedOperation?.id,
      resourceType: CROWD_CLIENT_RESOURCE_TYPE.anonymousFlag,
      resourceId: flag.flagId,
      metadata: { sourcePage: '/flags', targetPage: '/reports', templateId: 'flagged_device_activity_summary' },
    });
    const params = new URLSearchParams({
      template: 'flagged_device_activity_summary',
      flagId: flag.flagId,
    });
    if (selectedOperation?.id) params.set('operationId', selectedOperation.id);
    navigate(`/reports?${params.toString()}`);
  }
  return (
    <div style={{
      flex: 1,
      display: 'flex',
      overflow: 'hidden',
      fontFamily: "'Albert Sans', sans-serif",
      background: C.bg,
    }}>
      <aside style={{
        width: SIZE.alertColumn,
        background: C.surface,
        borderRight: `1px solid ${C.border}`,
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
      }}>
        <div style={{ padding: `${SPACE[14]}px ${SPACE[18]}px`, borderBottom: `1px solid ${C.border2}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: SPACE[8] }}>
            <div>
              <div style={{ fontSize: FONT_SIZE.title, fontWeight: FONT_WEIGHT.bold, color: C.text }}>Flagged Tokens</div>
              <div style={{ fontSize: FONT_SIZE.sm, color: C.muted, marginTop: SPACE[2] }}>Session-bound flags</div>
            </div>
            <span style={{
              fontSize: FONT_SIZE.xs,
              color: flagCountColor(scopedFlags.length, C),
              fontFamily: 'monospace',
              fontWeight: FONT_WEIGHT.bold,
            }}>
              {scopedFlags.length} / {FLAG_LIMIT}
            </span>
          </div>

          <div style={{
            height: SIZE.capacityBar,
            background: C.faint,
            borderRadius: RADIUS.xs,
            overflow: 'hidden',
            marginBottom: SPACE[6],
          }}>
            <div style={{
              height: '100%',
              width: `${(scopedFlags.length / FLAG_LIMIT) * 100}%`,
              background: capColor(scopedFlags.length, C),
            }} />
          </div>
          <div style={{ fontSize: FONT_SIZE.xs, color: C.muted, marginBottom: SPACE[12] }}>
            {scopedFlags.length} of {FLAG_LIMIT} session flags active
          </div>

          <div style={{ display: 'flex', gap: SPACE[6], marginBottom: SPACE[12] }}>
            {PRIORITY_FILTERS.map(item => (
              <button
                key={item}
                type="button"
                onClick={() => setFilter(item)}
                style={{
                  background: filter === item ? C.amber : C.surface,
                  color: filter === item ? '#fff' : C.sub,
                  border: `1px solid ${filter === item ? C.amber : C.border2}`,
                  borderRadius: RADIUS.pill,
                  cursor: 'pointer',
                  fontFamily: "'Albert Sans', sans-serif",
                  fontSize: FONT_SIZE.base,
                  fontWeight: FONT_WEIGHT.bold,
                  padding: `${SPACE[5]}px ${SPACE[10]}px`,
                  textTransform: 'capitalize',
                }}
              >
                {priorityLabel(item)}
              </button>
            ))}
          </div>

          {canCreate && (
            <Btn
              C={C}
              variant="primary"
              size="sm"
              icon={<Icons.Plus size={SIZE.iconSm} />}
              disabled={atLimit}
              style={{ width: '100%', justifyContent: 'center' }}
              onClick={() => {
                if (atLimit) {
                  show(`Session flag limit reached (${FLAG_LIMIT}/${FLAG_LIMIT})`, 'warning');
                  return;
                }
                recordActivity({
                  action: CROWD_CLIENT_ACTIVITY_ACTION.drilldownOpened,
                  page: '/flags',
                  operationId: selectedOperation?.id,
                  resourceType: CROWD_CLIENT_RESOURCE_TYPE.anonymousFlag,
                  resourceId: 'new-flag',
                  metadata: { sourcePage: '/flags', targetPage: '/flags' },
                });
                setInitialCreateZoneId(null);
                setShowCreate(true);
              }}
            >
              {atLimit ? 'Session limit reached' : 'Flag Token'}
            </Btn>
          )}
        </div>

        <div style={{ flex: 1, overflowY: 'auto' }}>
          {visibleFlags.map(flag => (
            <FlagRow
              key={flag.flagId}
              flag={flag}
              activity={flagActivity[flag.flagId]}
              zones={zones}
              selected={flag.flagId === selectedId}
              C={C}
              onClick={() => {
                setSelectedId(flag.flagId);
                recordActivity({
                  action: CROWD_CLIENT_ACTIVITY_ACTION.recordViewed,
                  page: '/flags',
                  operationId: selectedOperation?.id,
                  resourceType: CROWD_CLIENT_RESOURCE_TYPE.anonymousFlag,
                  resourceId: flag.flagId,
                  metadata: { sourcePage: '/flags' },
                });
              }}
            />
          ))}
        </div>

        <PaginationControls
          C={C}
          pagination={flagPagination.pagination}
          loading={flagState.loading}
          error={flagState.error}
          emptyLabel={flagState.notStarted ? 'This event has not started yet. Flagged tokens appear here once it is live.' : 'No flagged tokens match this filter.'}
          onPageChange={flagPagination.setPage}
          onPageSizeChange={flagPagination.setPageSize}
        />

        <div style={{ padding: SPACE[12], borderTop: `1px solid ${C.border}`, background: C.surface }}>
          <div style={{ fontSize: FONT_SIZE.micro, color: C.muted, lineHeight: LINE_HEIGHT.relaxed }}>
            Flags expire at session end. Maximum 20 active flags per session. Anonymous behaviour tokens only - never linked to subscriber identity.
          </div>
        </div>
      </aside>

      <main style={{ flex: 1, overflowY: 'auto', padding: SPACE[24], background: C.bg }}>
        {selectedFlag ? (
          <FlagDetail
            flag={selectedFlag}
            activity={flagActivity[selectedFlag.flagId]}
            zones={zones}
            density={density}
            updateFlag={updateFlag}
            removeFlag={removeFlag}
            exportLog={exportLog}
            canManage={canManage}
            C={C}
          />
        ) : (
          <EmptyState C={C} />
        )}
      </main>

      {showCreate && (
        <CreateFlagModal
          C={C}
          zones={zones}
          density={density}
          initialZoneId={initialCreateZoneId}
          onClose={() => {
            setShowCreate(false);
            setInitialCreateZoneId(null);
          }}
          onCreate={async dto => {
            const flag = await createFlag(dto);
            setSelectedId(flag.flagId);
          }}
        />
      )}
      <ToastContainer toasts={toasts} onDismiss={dismiss} C={C} />
    </div>
  );
}
